import { ComponentType, SVGProps, useEffect, useState } from "react";
import clsx from "clsx";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  SignalSlashIcon,
  StopCircleIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";

import { IImportSyncRun, ImportSyncStatus } from "../../models/ImportSync/ImportSync";

/**
 * What a run is showing as. "Stalled" is not one of Core's statuses — it is a Running run the
 * console has stopped reporting for — but it is what the reader needs to see.
 */
export type SyncDisplayState = ImportSyncStatus | "Stalled";

export function displayState(run: Pick<IImportSyncRun, "status" | "isStalled">): SyncDisplayState {
  return run.status === "Running" && run.isStalled ? "Stalled" : run.status;
}

interface StateMeta {
  label: string;
  description: string;
  pill: string;
  /** Mark colour for the run strip. Status steps from the reserved status palette. */
  swatch: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  spin?: boolean;
}

export const stateMeta: Record<SyncDisplayState, StateMeta> = {
  Running: {
    label: "Running",
    description: "In progress",
    pill: "bg-blue-100 text-blue-700",
    swatch: "#3b82f6",
    icon: ArrowPathIcon,
    spin: true,
  },
  Succeeded: {
    label: "Succeeded",
    description: "Every vehicle imported",
    pill: "bg-green-100 text-green-700",
    swatch: "#0ca30c",
    icon: CheckCircleIcon,
  },
  CompletedWithErrors: {
    label: "Completed with errors",
    description: "Finished; failed vehicles are retried next run",
    pill: "bg-amber-100 text-amber-800",
    swatch: "#fab219",
    icon: ExclamationTriangleIcon,
  },
  Stalled: {
    label: "Stalled",
    description: "Stopped reporting in",
    pill: "bg-orange-100 text-orange-800",
    swatch: "#ec835a",
    icon: PauseCircleIcon,
  },
  Abandoned: {
    label: "Abandoned",
    description: "Went quiet; resumed by the next run",
    pill: "bg-orange-100 text-orange-800",
    swatch: "#ec835a",
    icon: SignalSlashIcon,
  },
  Failed: {
    label: "Failed",
    description: "Stopped by an error; resumed by the next run",
    pill: "bg-red-100 text-red-700",
    swatch: "#d03b3b",
    icon: XCircleIcon,
  },
  Cancelled: {
    label: "Cancelled",
    description: "Stopped on purpose; resumed by the next run",
    pill: "bg-gray-100 text-gray-700",
    swatch: "#9ca3af",
    icon: StopCircleIcon,
  },
};

export function SyncStatePill({ state, className }: { state: SyncDisplayState; className?: string }) {
  const meta = stateMeta[state];
  const Icon = meta.icon;

  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap",
        meta.pill,
        className,
      )}
    >
      <Icon className={clsx("h-3.5 w-3.5", meta.spin && "animate-spin")} aria-hidden />
      {meta.label}
    </span>
  );
}

/** Re-renders on an interval so "12s ago" keeps counting between refetches. */
export function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return now;
}

export function formatDuration(ms: number) {
  if (!isFinite(ms) || ms < 0) return "—";

  const totalMinutes = Math.floor(ms / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (totalMinutes > 0) return `${minutes}m`;

  return `${Math.max(1, Math.floor(ms / 1000))}s`;
}

export function formatAgo(iso: string | null | undefined, now: number) {
  if (!iso) return "never";

  const ms = now - new Date(iso).getTime();
  if (ms < 5_000) return "just now";

  return `${formatDuration(ms)} ago`;
}

export function formatDateTime(iso: string | null | undefined) {
  if (!iso) return "—";

  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Pinnacle's own clock — the sync window is recorded in it, with no time zone. Shown as written
 * rather than converted, since it is the yard's local time.
 */
export function formatSourceTime(value: string | null | undefined) {
  if (!value) return "—";

  const [date, time] = value.split("T");
  const [y, m, d] = date.split("-").map(Number);
  const month = new Date(y, m - 1, d).toLocaleString("en-GB", { month: "short" });

  return `${d} ${month} ${time?.slice(0, 5) ?? ""}`.trim();
}

export function runDurationMs(run: IImportSyncRun, now: number) {
  const end = run.completedOn ? new Date(run.completedOn).getTime() : now;
  return end - new Date(run.startedOn).getTime();
}

export const numberFormat = new Intl.NumberFormat("en-GB");
